import { useCallback, useEffect, useState } from "preact/hooks";
import { tr } from "../i18n.js";
import { del, fmtDateTime, get, html, patch, post } from "../lib.js";
import { Badge, Empty, Field, Loading, Modal, PageHeader, toast, useAction, useLoad, usePoll } from "../ui.js";
import { PageGuide } from "../guide.js";

const pingAttention = () => window.dispatchEvent(new Event("rz:attention"));

export const CATEGORIES = ["customer_new", "customer_existing", "supplier", "billing", "notification", "marketing", "spam", "other"];
const CATEGORY_LABEL = {
  customer_new: tr("New customer"),
  customer_existing: tr("Existing customer"),
  supplier: tr("Supplier"),
  billing: tr("Invoice / payment"),
  notification: tr("Notification"),
  marketing: tr("Newsletter / promotion"),
  spam: tr("Spam"),
  other: tr("Other"),
};
const CATEGORY_KIND = { customer_new: "accent", customer_existing: "accent", supplier: "ok", billing: "ok", notification: "muted", marketing: "muted", spam: "warn", other: "neutral" };
const ACTION_LABEL = { alert: tr("Alert the team"), inbox: tr("List it"), archive: tr("File it away") };
const VIEWS = [
  { key: "todo", label: tr("To handle") },
  { key: "filtered", label: tr("Filtered") },
  { key: "all", label: tr("All") },
];

const REPLY_MODES = [
  { key: "off", label: tr("Off"), hint: tr("Nothing is written. Customers' emails wait for you under To handle.") },
  { key: "draft", label: tr("Draft"), hint: tr("The AI writes a reply to each customer's email from your agent's knowledge. You read it, change anything, and press Send.") },
  {
    key: "auto",
    label: tr("Automatic"),
    hint: tr("Replies go out by themselves when the email is plainly a customer's, the sender is a customer on file or their email provider verified them, and nothing about it needs a person. Complaints, urgent emails and anything the AI isn't sure about stay as drafts for you."),
  },
];

const replyBadge = (e) =>
  e.reply_status === "sent"
    ? html` <${Badge} kind="ok">${e.reply_sent_by === "auto" ? tr("Answered by AI") : tr("Replied")}</${Badge}>`
    : e.reply_status === "draft"
      ? html` <${Badge} kind="accent">${tr("Reply ready")}</${Badge}>`
      : e.reply_status === "failed"
        ? html` <${Badge} kind="warn">${tr("Reply not sent")}</${Badge}>`
        : "";

const categoryBadge = (e) => html`${e.urgent ? html`<${Badge} kind="warn">${tr("Urgent")}</${Badge}> ` : ""}${
  e.category
    ? html`<${Badge} kind=${CATEGORY_KIND[e.category]}>${CATEGORY_LABEL[e.category]}</${Badge}>`
    : html`<${Badge} kind="warn">${tr("Not sorted")}</${Badge}>`
}`;
const REMINDER_HOURS = [0, 1, 2, 4, 8, 24];

/** Gmail can find a message by its Message-ID, so "open in Gmail" lands on the original. */
const gmailLink = (messageId) =>
  messageId && !messageId.endsWith("@digest>") ? `https://mail.google.com/mail/u/0/#search/rfc822msgid:${encodeURIComponent(messageId.replace(/^<|>$/g, ""))}` : null;

const fill = (s, parts) => s.split(/\{(\w+)\}/).map((x, i) => (i % 2 ? parts[x] : x));

export function EmailPage({ t }) {
  const [state, reloadState] = useLoad(() => get(`${t.base}/email`), [t.id]);
  const canEdit = t.role !== "agent";
  return html`<div>
    <${PageHeader} title=${tr("Email")} subtitle=${tr("Your mailbox, sorted: customers first, suppliers and invoices listed, the rest filed away")} />
    <${PageGuide} id="email" />
    ${!state && html`<${Loading} />`}
    ${state && !state.available && html`<div class="callout">${tr("Email sorting isn't switched on for this platform yet.")}</div>`}
    ${state?.available && !state.inbox && html`<${StartCard} t=${t} canEdit=${canEdit} onDone=${reloadState} />`}
    ${state?.inbox && html`
      <${SetupCard} t=${t} inbox=${state.inbox} canEdit=${canEdit} onChange=${reloadState} />
      <${RepliesCard} t=${t} inbox=${state.inbox} replyTo=${state.reply_to} canEdit=${canEdit} onChange=${reloadState} />
      <${EmailList} t=${t} counts=${state.counts} onChange=${reloadState} />
      <${PolicyCard} t=${t} inbox=${state.inbox} canEdit=${canEdit} onChange=${reloadState} />
      <${RulesCard} t=${t} canEdit=${canEdit} />`}
  </div>`;
}

function StartCard({ t, canEdit, onDone }) {
  const [busy, run] = useAction();
  const start = async () => {
    await run(() => post(`${t.base}/email/inbox`), tr("Forwarding address created"));
    onDone();
  };
  return html`<section class="card">
    <h2>${tr("Sort your email automatically")}</h2>
    <p class="small">${tr("Forward your business mailbox to an address of ours. Every email is sorted as it arrives: new and existing customers, suppliers, invoices, notifications, newsletters and spam. Customers alert the team and get a reply drafted for you to send; the rest waits in a list or is filed away. Nothing is deleted, and the original stays in your mailbox.")}</p>
    ${canEdit
      ? html`<button class="btn primary" disabled=${busy} onClick=${start}>${tr("Create my forwarding address")}</button>`
      : html`<p class="muted small">${tr("An owner or admin can switch this on.")}</p>`}
  </section>`;
}

function SetupCard({ t, inbox, canEdit, onChange }) {
  const [busy, run] = useAction();
  const receiving = !!inbox.last_received_at;
  const [open, setOpen] = useState(!receiving);
  const copy = () => navigator.clipboard.writeText(inbox.address).then(() => toast(tr("Copied")));
  const setStatus = async (status) => {
    await run(() => patch(`${t.base}/email/inbox`, { status }), status === "paused" ? tr("Sorting paused") : tr("Sorting resumed"));
    onChange();
  };
  const dismiss = async () => {
    await run(() => patch(`${t.base}/email/inbox`, { dismiss_confirmation: true }));
    onChange();
  };
  const conf = inbox.gmail_confirmation;
  return html`<section class="card">
    <div class="row-between">
      <h2>${tr("Forwarding address")}</h2>
      <div class="row-actions">
        ${inbox.status === "paused" ? html`<${Badge} kind="muted">${tr("Paused")}</${Badge}>` : receiving ? html`<${Badge} kind="ok">${tr("Receiving")}</${Badge}>` : html`<${Badge} kind="warn">${tr("Waiting for the first email")}</${Badge}>`}
        ${canEdit && html`<button class="btn small" disabled=${busy} onClick=${() => setStatus(inbox.status === "paused" ? "active" : "paused")}>${inbox.status === "paused" ? tr("Resume") : tr("Pause")}</button>`}
      </div>
    </div>
    <pre class="code" dir="ltr">${inbox.address}</pre>
    <button class="btn small" onClick=${copy}>${tr("Copy address")}</button>
    ${receiving && html`<p class="muted small">${tr("Last email: {when}", { when: fmtDateTime(inbox.last_received_at, t.timezone) })}</p>`}
    ${conf && html`<div class="callout">
      <strong>${tr("Gmail is asking you to confirm")}</strong>
      <p class="small">${conf.requested_by ? tr("{who} asked to forward its mail here. Open the link to allow it, then come back.", { who: conf.requested_by }) : tr("Your mailbox asked to forward its mail here. Open the link to allow it, then come back.")}</p>
      ${conf.url && html`<a class="btn primary small" href=${conf.url} target="_blank" rel="noopener" onClick=${() => setTimeout(dismiss, 1500)}>${tr("Confirm in Gmail")}</a>`}
      ${conf.code && html`<p class="small">${fill(tr("Or type this code in Gmail's settings: {code}"), { code: html`<code dir="ltr">${conf.code}</code>` })}</p>`}
    </div>`}
    <details open=${open} onToggle=${(e) => setOpen(e.target.open)}>
      <summary>${tr("How to forward from Gmail")}</summary>
      <ol class="small">
        <li>${tr("In Gmail on a computer, open Settings (the gear) → See all settings → Forwarding and POP/IMAP.")}</li>
        <li>${tr("Choose Add a forwarding address and paste the address above. Gmail sends us a confirmation; within a minute it shows up on this page with a button.")}</li>
        <li>${tr("Press Confirm in Gmail here, then back in Gmail's settings choose Forward a copy of incoming mail and keep Gmail's copy in the Inbox. Save.")}</li>
        <li>${tr("Gmail doesn't forward what it already put in Spam. To forward only part of your mail, use a Gmail filter with Forward it instead.")}</li>
        <li>${tr("Google Workspace: if forwarding is greyed out, your admin has to allow automatic forwarding for your account.")}</li>
      </ol>
    </details>
  </section>`;
}

function RepliesCard({ t, inbox, replyTo, canEdit, onChange }) {
  const [busy, run] = useAction();
  const mode = REPLY_MODES.find((m) => m.key === inbox.reply_mode) ?? REPLY_MODES[1];
  const set = async (reply_mode) => {
    await run(() => patch(`${t.base}/email/inbox`, { reply_mode }), tr("Saved"));
    onChange();
  };
  return html`<section class="card">
    <div class="row-between">
      <h2>${tr("Replies to customers")}</h2>
      <select class="email-policy" aria-label=${tr("Replies to customers")} disabled=${!canEdit || busy} value=${mode.key} onChange=${(e) => set(e.target.value)}>
        ${REPLY_MODES.map((m) => html`<option value=${m.key}>${m.label}</option>`)}
      </select>
    </div>
    <p class="muted small">${mode.hint}</p>
    ${mode.key !== "off" && html`<p class="muted small">${replyTo
      ? fill(tr("Replies are sent under your business name, and when customers answer, it reaches {address}."), { address: html`<strong dir="ltr">${replyTo}</strong>` })
      : tr("Add a customer contact email under Settings → Business so customers' answers reach you.")}</p>`}
  </section>`;
}

function EmailList({ t, counts, onChange }) {
  const [view, setView] = useState("todo");
  const [category, setCategory] = useState("");
  const [open, setOpen] = useState(null);
  // "Show more" widens the window instead of paging it, so the 30-second refresh keeps every row shown.
  const [shown, setShown] = useState(100);
  useEffect(() => setShown(100), [view, category]);
  const [list, reload] = useLoad(() => get(`${t.base}/email/messages?${new URLSearchParams({ view, limit: String(shown), ...(category && { category }) })}`), [t.id, view, category, shown]);
  usePoll(useCallback(reload, [view, category]), 30_000);
  const [busy, run] = useAction();
  const refresh = () => {
    reload();
    onChange();
    pingAttention();
  };
  const setStatus = async (e, status) => {
    await run(() => patch(`${t.base}/email/messages/${e.id}`, { status }));
    refresh();
  };
  return html`<section class="card">
    <div class="tabs" role="tablist">
      ${VIEWS.map((v) => html`<button role="tab" aria-selected=${view === v.key} class=${view === v.key ? "active" : ""} onClick=${() => setView(v.key)}>
        ${v.label}${v.key === "todo" && counts.todo ? html` <span class="count">${counts.todo}</span>` : ""}
      </button>`)}
    </div>
    <div class="chips">
      <button class=${!category ? "chip active" : "chip"} onClick=${() => setCategory("")}>${tr("Every kind")}</button>
      ${CATEGORIES.map((c) => html`<button class=${category === c ? "chip active" : "chip"} onClick=${() => setCategory(c)}>${CATEGORY_LABEL[c]}</button>`)}
      ${counts.unsorted ? html`<button class=${category === "unsorted" ? "chip active" : "chip"} onClick=${() => setCategory("unsorted")}>${tr("Not sorted")} (${counts.unsorted})</button>` : ""}
    </div>
    ${!list && html`<${Loading} />`}
    ${list && !list.emails.length && html`<${Empty} title=${view === "todo" ? tr("Nothing waiting") : tr("No email here")}>${view === "todo" ? tr("Customer emails and anything else that needs a person show up here as they arrive.") : tr("Emails appear here once your mailbox forwards to the address above.")}</${Empty}>`}
    ${list?.emails.length > 0 && html`<ul class="plain email-rows">${list.emails.map((e) => html`<li class="email-row">
      <div class="email-main">
        <button class="link" onClick=${() => setOpen(e)}><strong dir="auto">${e.from_name || e.from_address}</strong></button>
        <div dir="auto">${e.subject || tr("(no subject)")}</div>
        <div class="muted small" dir="auto">${e.summary || e.triage_error || e.snippet}</div>
      </div>
      <div class="email-meta">
        ${categoryBadge(e)}${replyBadge(e)}
        <span class="nowrap small muted">${fmtDateTime(e.received_at, t.timezone)}</span>
        ${e.status === "new"
          ? html`<button class="btn small" disabled=${busy} onClick=${() => setStatus(e, "done")}>${tr("Done")}</button>`
          : html`<button class="btn small ghost" disabled=${busy} onClick=${() => setStatus(e, "new")}>${tr("Back to the list")}</button>`}
      </div>
    </li>`)}</ul>`}
    ${list?.has_more && shown < 1000 && html`<p class="center"><button class="btn small ghost" onClick=${() => setShown((n) => n + 100)}>${tr("Show more")}</button></p>`}
    ${open && html`<${EmailDetail} t=${t} id=${open.id} onClose=${() => setOpen(null)} onChange=${refresh} />`}
  </section>`;
}

function EmailDetail({ t, id, onClose, onChange }) {
  const [data, reloadDetail] = useLoad(() => get(`${t.base}/email/messages/${id}`), [id]);
  const [category, setCategory] = useState(null);
  const [remember, setRemember] = useState("");
  const [busy, run] = useAction();
  const e = data?.email;
  const chosen = category ?? e?.category ?? "";
  const save = async () => {
    await run(() => patch(`${t.base}/email/messages/${id}`, { category: chosen, ...(remember && { remember }) }), tr("Saved"));
    onChange();
    onClose();
  };
  const sortAgain = async () => {
    const r = await run(() => post(`${t.base}/email/messages/${id}/sort`));
    if (r.error) toast(r.error, "error");
    else toast(tr("Sorted"));
    onChange();
    onClose();
  };
  const link = e && gmailLink(e.message_id);
  return html`<${Modal} title=${tr("Email")} onClose=${onClose} wide>
    ${!e && html`<${Loading} />`}
    ${e && html`
      <p><strong dir="auto">${e.from_name || e.from_address}</strong> <span class="muted small" dir="ltr">${e.from_address}</span></p>
      <p class="small"><strong dir="auto">${e.subject || tr("(no subject)")}</strong> · <span class="muted">${fmtDateTime(e.received_at, t.timezone)}</span>${e.attachments ? html` · <span class="muted">${tr("{n} attachments", { n: e.attachments })}</span>` : ""}</p>
      <p>${categoryBadge(e)} ${e.sorted_by && html`<span class="muted small">${{ rule: tr("by your sender rule"), headers: tr("automatically, from the email's headers"), model: tr("by AI"), staff: tr("by your team") }[e.sorted_by]}</span>`}</p>
      ${e.summary && html`<p class="small" dir="auto">${e.summary}</p>`}
      ${e.triage_error && html`<div class="callout small">${e.triage_error}</div>`}
      ${!e.category && html`<p><button class="btn small" disabled=${busy} onClick=${sortAgain}>${tr("Sort again")}</button></p>`}
      ${e.contact_id && html`<p class="small">${tr("This sender is already one of your contacts.")}</p>`}
      <pre class="email-body" dir="auto">${e.body_text ?? (e.signals?.truncated ? tr("This email was too large to read here; open it in your mailbox.") : tr("The text of this email is no longer kept; open it in your mailbox."))}</pre>
      ${link && html`<p><a href=${link} target="_blank" rel="noopener">${tr("Open in Gmail")}</a></p>`}
      <${ReplyEditor} key=${data.reply?.id ?? "none"} t=${t} email=${e} reply=${data.reply} onChange=${() => { reloadDetail(); onChange(); }} />
      <hr />
      <${Field} label=${tr("This email is")}>
        <select value=${chosen} onChange=${(ev) => setCategory(ev.target.value)}>
          ${!e.category && html`<option value="">${tr("Choose…")}</option>`}
          ${CATEGORIES.map((c) => html`<option value=${c}>${CATEGORY_LABEL[c]}</option>`)}
        </select>
      </${Field}>
      <${Field} label=${tr("Next time")}>
        <select value=${remember} onChange=${(ev) => setRemember(ev.target.value)}>
          <option value="">${tr("Let the sorter decide")}</option>
          <option value="address">${tr("Always sort {address} this way", { address: e.from_address })}</option>
          <option value="domain">${tr("Always sort everyone at {domain} this way", { domain: e.from_address.slice(e.from_address.lastIndexOf("@") + 1) })}</option>
        </select>
      </${Field}>
      <button class="btn primary" disabled=${busy || !chosen} onClick=${save}>${tr("Save")}</button>`}
  </${Modal}>`;
}

/** The reply to one email: write it, edit and send it, or see what went out. */
function ReplyEditor({ t, email, reply, onChange }) {
  const [subject, setSubject] = useState(reply?.subject ?? "");
  const [body, setBody] = useState(reply?.body ?? "");
  const [busy, run] = useAction();
  const isCustomer = email.category === "customer_new" || email.category === "customer_existing";
  const write = async () => {
    await run(() => post(`${t.base}/email/messages/${email.id}/reply`), tr("Reply written"));
    onChange();
  };
  const send = async () => {
    await run(() => post(`${t.base}/email/replies/${reply.id}/send`, { subject, body }), tr("Reply sent"));
    onChange();
  };
  const discard = async () => {
    await run(() => post(`${t.base}/email/replies/${reply.id}/discard`), tr("Reply discarded"));
    onChange();
  };
  if (reply?.status === "sent") {
    return html`<div class="email-reply">
      <h3>${reply.sent_by === "auto" ? tr("Answered by AI") : tr("Replied")} · <span class="muted small">${fmtDateTime(reply.sent_at, t.timezone)}</span></h3>
      <pre class="email-body" dir="auto">${reply.body}</pre>
    </div>`;
  }
  if (!reply || reply.status === "discarded") {
    return isCustomer ? html`<p><button class="btn small" disabled=${busy} onClick=${write}>${busy ? tr("Writing…") : tr("Write a reply with AI")}</button></p>` : "";
  }
  return html`<div class="email-reply">
    <h3>${tr("Reply")}</h3>
    ${reply.needs_person ? html`<div class="callout warn small">${tr("Check this one before sending")}${reply.note ? `: ${reply.note}` : ""}</div>` : ""}
    ${reply.status === "failed" && html`<div class="callout error small">${tr("Not sent: {reason}", { reason: reply.error ?? "" })}</div>`}
    <p class="muted small" dir="ltr">${tr("To")}: ${reply.recipient}</p>
    <${Field} label=${tr("Subject")}><input dir="auto" value=${subject} onInput=${(ev) => setSubject(ev.target.value)} /></${Field}>
    <textarea dir="auto" rows="8" value=${body} onInput=${(ev) => setBody(ev.target.value)}></textarea>
    <div class="row-actions">
      <button class="btn primary" disabled=${busy || !body.trim()} onClick=${send}>${tr("Send reply")}</button>
      <button class="btn ghost" disabled=${busy} onClick=${discard}>${tr("Discard")}</button>
    </div>
  </div>`;
}

function PolicyCard({ t, inbox, canEdit, onChange }) {
  const [busy, run] = useAction();
  const set = async (category, action) => {
    await run(() => patch(`${t.base}/email/inbox`, { policy: { [category]: action } }), tr("Saved"));
    onChange();
  };
  const setReminder = async (hours) => {
    await run(() => patch(`${t.base}/email/inbox`, { reply_reminder_hours: hours }), tr("Saved"));
    onChange();
  };
  return html`<section class="card">
    <h2>${tr("What happens to each kind")}</h2>
    <p class="muted small">${tr("Alert the team: listed under To handle, and the team is told the way handoffs reach them. List it: listed under To handle. File it away: kept under Filtered, nobody is told.")}</p>
    <table class="table"><tbody>${CATEGORIES.map((c) => html`<tr>
      <td>${CATEGORY_LABEL[c]}</td>
      <td><select class="email-policy" aria-label=${CATEGORY_LABEL[c]} disabled=${!canEdit || busy} value=${inbox.policy[c]} onChange=${(e) => set(c, e.target.value)}>
        ${Object.entries(ACTION_LABEL).map(([a, label]) => html`<option value=${a}>${label}</option>`)}
      </select></td>
    </tr>`)}</tbody></table>
    <${Field} label=${tr("Remind the team when a customer waits")} hint=${tr("One reminder per email, outside quiet hours, until it is marked Done. Urgent emails alert the team right away, unless their kind is filed away.")}>
      <select class="email-policy" disabled=${!canEdit || busy} value=${String(inbox.reply_reminder_hours ?? 4)} onChange=${(e) => setReminder(Number(e.target.value))}>
        ${REMINDER_HOURS.map((h) => html`<option value=${String(h)}>${h === 0 ? tr("Never") : tr("After {n} hours", { n: h })}</option>`)}
      </select>
    </${Field}>
  </section>`;
}

function RulesCard({ t, canEdit }) {
  const [data, reload] = useLoad(() => get(`${t.base}/email/rules`), [t.id]);
  const [busy, run] = useAction();
  const remove = async (r) => {
    await run(() => del(`${t.base}/email/rules/${r.id}`), tr("Rule removed"));
    reload();
  };
  return html`<section class="card">
    <h2>${tr("Sender rules")}</h2>
    <p class="muted small">${tr("A rule wins over the AI every time. Add one from any email: open it, choose what it is and how to sort that sender next time.")}</p>
    ${data && !data.rules.length && html`<p class="muted small">${tr("No rules yet.")}</p>`}
    ${data?.rules.length > 0 && html`<table class="table"><tbody>${data.rules.map((r) => html`<tr>
      <td dir="ltr">${r.pattern.startsWith("@") ? `*${r.pattern}` : r.pattern}</td>
      <td>${CATEGORY_LABEL[r.category] ?? r.category}</td>
      <td class="row-actions">${canEdit && html`<button class="btn small ghost" disabled=${busy} onClick=${() => remove(r)}>${tr("Remove")}</button>`}</td>
    </tr>`)}</tbody></table>`}
  </section>`;
}
